import React, { useState, useEffect } from 'react';
import {
    Container,
    Typography,
    Box,
    Grid,
    Card,
    CardContent,
    CardActionArea,
    Avatar,
    Chip,
    Alert,
    CircularProgress,
    Button,
} from '@mui/material';
import {
    ArrowForward as ArrowForwardIcon,
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { memberProfileAPI } from '../../services/api';
import { MemberProfile } from '../../types/memberProfile';
import { useNavigate } from 'react-router-dom';
import { useLocation } from 'react-router-dom';

const SelectMemberProfilePage: React.FC = () => {
    const { user, setSelectedMemberProfile, selectedMemberProfile } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [memberProfiles, setMemberProfiles] = useState<MemberProfile[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [previouslySelectedProfile] = useState(selectedMemberProfile); // Capture the initial state

    useEffect(() => {
        const fetchMemberProfiles = async () => {
            if (!user?.id) return;

            try {
                setLoading(true);
                const response = await memberProfileAPI.getUserMemberProfiles(user.id);
                const allProfiles = response.data || [];
                
                // Filter to only show active, pending, on_hold, or suspended profiles
                const allowedStatuses = ['active', 'pending', 'on_hold', 'suspended'];
                const profiles = allProfiles.filter((profile: MemberProfile) => 
                    allowedStatuses.includes(profile.status.toLowerCase())
                );
                
                setMemberProfiles(profiles);

                if (profiles.length === 0) {
                    setError('No active member profiles found. Please contact an administrator.');
                }
                // Note: We removed auto-selection for single profile to avoid navigation loops
                // The Dashboard component handles single profile auto-selection
            } catch (err: any) {
                console.error('Error fetching member profiles:', err);
                setError(err.response?.data?.message || 'Failed to load member profiles');
            } finally {
                setLoading(false);
            }
        };

        fetchMemberProfiles();
    }, [user?.id]);

    const getStatusColor = (status: string) => {
        switch (status.toLowerCase()) {
            case 'active':
                return 'success';
            case 'expired':
            case 'cancelled':
                return 'error';
            case 'suspended':
            case 'on_hold':
                return 'warning';
            case 'pending':
                return 'info';
            default:
                return 'default';
        }
    };

    const getStatusLabel = (status: string) => {
        return status.charAt(0).toUpperCase() + status.slice(1).replace('_', ' ');
    };

    const handleProfileSelect = async (memberProfile: MemberProfile) => {
        try {
            // Record profile access on backend
            await memberProfileAPI.recordProfileAccess(memberProfile.id);
        } catch (error) {
            console.error('Error recording profile access:', error);
            // Don't block profile selection if recording fails
        }
        
        // Store the selected member profile in the auth context
        setSelectedMemberProfile(memberProfile);
        
        // Store in localStorage for persistence
        localStorage.setItem('selectedMemberProfile', JSON.stringify(memberProfile));
        
        // Navigate to the dashboard to show member dashboard with selected profile
        navigate('/dashboard');
    };

    const handleCancel = () => {
        // Restore the previous selection if user cancels
        if (previouslySelectedProfile) {
            setSelectedMemberProfile(previouslySelectedProfile);
            localStorage.setItem('selectedMemberProfile', JSON.stringify(previouslySelectedProfile));
        }
        navigate('/dashboard');
    };

    if (loading) {
        return (
            <Container maxWidth="md" sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress />
            </Container>
        );
    }

    if (error) {
        return (
            <Container maxWidth="md" sx={{ py: 4 }}>
                <Alert severity="error">{error}</Alert>
                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                    <Button variant="outlined" onClick={() => navigate('/dashboard')}>
                        Go to Dashboard
                    </Button>
                </Box>
            </Container>
        );
    }

    if (!user) {
        return (
            <Container maxWidth="md" sx={{ py: 4 }}>
                <Alert severity="error">User information not available</Alert>
            </Container>
        );
    }

    return (
        <Container maxWidth="md" sx={{ py: 4 }}>
            <Typography variant="h4" gutterBottom align="center">
                {previouslySelectedProfile ? 'Switch Member Profile' : 'Select Member Profile'}
            </Typography>
            <Typography variant="body1" color="text.secondary" align="center" sx={{ mb: 4 }}>
                {previouslySelectedProfile 
                    ? 'Choose a different member profile to switch to.'
                    : 'You have multiple member profiles associated with your account. Please select one to continue.'
                }
            </Typography>
            {previouslySelectedProfile && (
                <Box sx={{ mb: 3, p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
                    <Typography variant="body2" align="center" color="info.contrastText">
                        Previously selected: {previouslySelectedProfile.firstName} {previouslySelectedProfile.lastName}
                        {previouslySelectedProfile.membershipNumber && ` (Membership #${previouslySelectedProfile.membershipNumber})`}
                    </Typography>
                </Box>
            )}

            <Grid container spacing={3}>
                {memberProfiles.map((memberProfile) => (
                    <Grid size={{ xs: 12, sm: 6 }} key={memberProfile.id}>
                        <Card sx={{ 
                            height: '100%',
                            border: previouslySelectedProfile?.id === memberProfile.id ? 2 : 0,
                            borderColor: previouslySelectedProfile?.id === memberProfile.id ? 'primary.main' : 'transparent'
                        }}>
                            <CardActionArea 
                                onClick={() => handleProfileSelect(memberProfile)}
                                sx={{ height: '100%', p: 2 }}
                            >
                                <CardContent sx={{ textAlign: 'center' }}>
                                    <Box sx={{ mb: 2 }}>
                                        <Avatar
                                            sx={{
                                                width: 80,
                                                height: 80,
                                                mx: 'auto',
                                                bgcolor: 'primary.main',
                                                fontSize: '1.5rem',
                                                mb: 2
                                            }}
                                        >
                                            {memberProfile.firstName?.charAt(0)}{memberProfile.lastName?.charAt(0)}
                                        </Avatar>
                                        {previouslySelectedProfile?.id === memberProfile.id && (
                                            <Chip
                                                label="Current"
                                                color="primary"
                                                size="small"
                                                sx={{ position: 'absolute', top: 8, right: 8 }}
                                            />
                                        )}
                                    </Box>
                                    
                                    <Typography variant="h6" gutterBottom>
                                        {memberProfile.firstName} {memberProfile.lastName}
                                    </Typography>
                                    
                                    <Typography variant="body2" color="text.secondary" gutterBottom>
                                        Membership # {memberProfile.membershipNumber || 'N/A'}
                                    </Typography>
                                    
                                    <Box sx={{ mb: 2 }}>
                                        <Chip
                                            label={getStatusLabel(memberProfile.status)}
                                            color={getStatusColor(memberProfile.status) as any}
                                            variant="outlined"
                                            size="small"
                                        />
                                    </Box>
                                    
                                    <Typography variant="body2" color="text.secondary">
                                        {memberProfile.membershipTier?.name || 'No Tier Assigned'}
                                    </Typography>
                                    
                                    <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                                        <Typography variant="body2" color="primary">
                                            {previouslySelectedProfile?.id === memberProfile.id ? 'Current Profile' : 'Select Profile'}
                                        </Typography>
                                        {previouslySelectedProfile?.id !== memberProfile.id && (
                                            <ArrowForwardIcon color="primary" fontSize="small" />
                                        )}
                                    </Box>
                                </CardContent>
                            </CardActionArea>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            {/* Show Cancel button when switching profiles */}
            {previouslySelectedProfile && (
                <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
                    <Button 
                        variant="outlined" 
                        onClick={handleCancel}
                        sx={{ minWidth: 120 }}
                    >
                        Cancel
                    </Button>
                </Box>
            )}
        </Container>
    );
};

export default SelectMemberProfilePage;
